import { logger } from '../log';
import { createInitialState, symbolPickerReducer, updateUnderlyingSymbolPrices } from '../state';
import type { InstrumentState, InstrumentType, SymbolPickerAction, SymbolPickerState } from '../state';
import { signal } from '@preact/signals-react';
import type { ReadonlySignal, Signal } from '@preact/signals-react';
import { parseInstrument } from 'src/lib/instrumentUtils';
import type { MarketDataService } from 'src/services/MarketDataService';
import type { RecentlyUsedSymbolsService } from 'src/services/RecentlyUsedSymbolsService';

const log = logger.getSubLogger({ name: 'SymbolPickerService' });

const CANCELLATION_ERROR_MESSAGE = 'The market data request was cancelled';

export class SymbolPickerService {
    #recentlyUsedSymbolsService: RecentlyUsedSymbolsService;
    #marketDataService: MarketDataService | null;
    #currentMarketDataController: AbortController | null = null;

    #state: Signal<SymbolPickerState>;

    #onChange?: (newSymbol: string) => void;
    #resetOnChange: boolean;

    constructor({
        recentlyUsedSymbolsService,
        marketDataService,
        initialInstrumentString,
        initialAllowedInstrumentTypes,
        onChange,
        resetOnChange,
    }: {
        recentlyUsedSymbolsService: RecentlyUsedSymbolsService;
        marketDataService?: MarketDataService | null;
        initialInstrumentString: string;
        initialAllowedInstrumentTypes: InstrumentType[];
        onChange?: (newSymbol: string) => void;
        resetOnChange?: boolean;
    }) {
        this.#recentlyUsedSymbolsService = recentlyUsedSymbolsService;
        this.#marketDataService = marketDataService ?? null;
        this.#onChange = onChange;
        this.#resetOnChange = resetOnChange ?? false;

        this.#state = signal(createInitialState(initialInstrumentString, initialAllowedInstrumentTypes));

        // get market data for initial symbol
        this.#updateMarketData(this.#state.value.instrument);
    }

    get state(): ReadonlySignal<SymbolPickerState> {
        return this.#state;
    }

    dispatch(action: SymbolPickerAction) {
        const currentState = this.#state.value;
        const newState = symbolPickerReducer(currentState, action);

        this.#state.value = newState;

        if (
            newState.symbolString &&
            newState.isUserModified &&
            (currentState.symbolString !== newState.symbolString ||
                currentState.isUserModified !== newState.isUserModified)
        ) {
            this.#onChange?.(newState.symbolString);

            // Reset internal state after notifying parent (e.g. Watchlist adds symbols to a list)
            if (this.#resetOnChange) {
                this.#state.value = createInitialState('', newState.allowedInstrumentTypes);
                return;
            }
        }

        // Update recently used symbols when underlying symbol changes
        if (
            newState.instrument.underlyingSymbol !== currentState.instrument.underlyingSymbol &&
            newState.isUserModified
        ) {
            this.#recentlyUsedSymbolsService.add(newState.instrument.underlyingSymbol);
        }

        // Update market data subscription when underlying symbol OR instrument type changes
        if (
            newState.instrument.underlyingSymbol !== currentState.instrument.underlyingSymbol ||
            newState.instrument.instrumentType !== currentState.instrument.instrumentType
        ) {
            this.#updateMarketData(newState.instrument);
        }
    }

    async #updateMarketData(instrumentState: InstrumentState): Promise<void> {
        // Abort any existing market data request
        if (this.#currentMarketDataController) {
            this.#currentMarketDataController.abort(new Error(CANCELLATION_ERROR_MESSAGE));
            this.#currentMarketDataController = null;
        }

        // Only fetch if we have a valid underlying symbol and market data service
        if (!instrumentState || !instrumentState.underlyingSymbol || !this.#marketDataService) {
            return;
        }

        // Create new AbortController for this request
        const controller = new AbortController();
        this.#currentMarketDataController = controller;

        try {
            // Create equity instrument for the underlying symbol
            const instrument = parseInstrument(instrumentState.underlyingSymbol);

            // Fetch market data once
            const marketData = await this.#marketDataService.once(instrument, { signal: controller.signal });

            // Dispatch all three prices
            this.dispatch(updateUnderlyingSymbolPrices(marketData.bid, marketData.ask, marketData.last));

            if (TRACE) {
                log.trace('Updated market data', {
                    symbol: instrumentState.underlyingSymbol,
                    bid: marketData.bid,
                    ask: marketData.ask,
                    last: marketData.last,
                });
            }
        } catch (error) {
            // Don't log errors for aborted requests
            if (error instanceof Error && error.message === CANCELLATION_ERROR_MESSAGE) {
                return;
            }

            if (TRACE) {
                log.trace('Failed to fetch market data', {
                    symbol: instrumentState.underlyingSymbol,
                    error,
                });
            }

            throw error;
        } finally {
            // Clear the controller if this was the current one
            if (this.#currentMarketDataController === controller) {
                this.#currentMarketDataController = null;
            }
        }
    }

    [Symbol.dispose]() {
        // Abort any pending market data request
        if (this.#currentMarketDataController) {
            // Only abort if not already aborted to avoid "signal is aborted without reason" errors
            if (!this.#currentMarketDataController.signal.aborted) {
                this.#currentMarketDataController.abort(new Error(CANCELLATION_ERROR_MESSAGE));
            }
            this.#currentMarketDataController = null;
        }
    }
}
